import SettlementTypeSelect from "@/modules/projects/components/SettlementTypeSelect";
import { RoleBasedProjectSettlementTypeService } from "@/modules/projects/service/settlementType";
import { forwardRef, useEffect } from "react";
import { useFormContext, ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Module } from "@/constants";
import { ContractorVisitsService } from "@/modules/projects/services/contractorVisits";

interface PredefinedSettlementTypeSelectProps extends ControllerRenderProps {
  storeId: string;
  projectRoleId: string;
  className?: string;
}

export const PredefinedSettlementTypeSelect = forwardRef<
  HTMLButtonElement,
  PredefinedSettlementTypeSelectProps
>(({ projectRoleId, storeId, ...props }, ref) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const {
    data: predefinedSettlementType,
    isFetching,
    isFetched,
    isError,
  } = useQuery({
    queryKey: [
      Module.ContractorVisits,
      "settlement-type",
      projectRoleId,
      storeId,
    ],
    queryFn: () =>
      ContractorVisitsService.getSettlementType(projectRoleId, storeId),
    enabled: !!projectRoleId && !!storeId,
  });

  useEffect(() => {
    if (!isFetched) return;

    if (predefinedSettlementType) {
      form.setValue("settlement_type", predefinedSettlementType);
    }
  }, [isFetched, predefinedSettlementType, form]);

  const value = predefinedSettlementType ?? props.value;
  const selectedItem = isFetching
    ? { id: value, name: t("settlementType.loading") }
    : predefinedSettlementType
      ? { id: value, name: t(`settlementType.${predefinedSettlementType}`) }
      : undefined;
  const disabled =
    props.disabled ||
    isFetching ||
    !!predefinedSettlementType ||
    !projectRoleId ||
    !storeId ||
    isError;

  return (
    <SettlementTypeSelect
      settlementTypeService={RoleBasedProjectSettlementTypeService}
      className="w-full"
      enabled={Boolean(projectRoleId && storeId)}
      {...props}
      value={value ?? undefined}
      defaultItem={selectedItem}
      ref={ref}
      disabled={disabled}
    />
  );
});
